import { AwsRum } from 'aws-rum-web';

/**
 * AWS RUM (Real User Monitoring) Service
 * 
 * This service initializes and manages AWS CloudWatch RUM for tracking:
 * - Page load performance
 * - JavaScript errors
 * - HTTP requests and responses
 * - User interactions
 * - Custom events and metrics
 * - Square payment flow tracking
 */

class AWSRumService {
    constructor() {
        this.awsRum = null;
        this.isInitialized = false;
        this.config = {
            // AWS RUM Configuration
            applicationId: process.env.REACT_APP_AWS_RUM_APPLICATION_ID,
            applicationVersion: process.env.REACT_APP_VERSION || '1.0.0',
            applicationRegion: process.env.REACT_APP_AWS_RUM_REGION || 'us-east-1',
            
            // RUM Configuration
            sessionSampleRate: parseFloat(process.env.REACT_APP_AWS_RUM_SAMPLE_RATE) || 1.0,
            identityPoolId: process.env.REACT_APP_AWS_RUM_IDENTITY_POOL_ID,
            endpoint: process.env.REACT_APP_AWS_RUM_ENDPOINT,
            telemetries: process.env.REACT_APP_AWS_RUM_TELEMETRIES ? 
                process.env.REACT_APP_AWS_RUM_TELEMETRIES.split(',') : 
                ['performance', 'errors', 'http'],
            allowCookies: process.env.REACT_APP_AWS_RUM_ALLOW_COOKIES !== 'false',
            enableXRay: process.env.REACT_APP_AWS_RUM_ENABLE_XRAY !== 'false'
        };

        console.log('[AWSRumService] Configuration loaded:', {
            applicationId: this.config.applicationId ? 'configured' : 'missing',
            applicationVersion: this.config.applicationVersion,
            applicationRegion: this.config.applicationRegion,
            sessionSampleRate: this.config.sessionSampleRate,
            identityPoolId: this.config.identityPoolId ? 'configured' : 'missing',
            telemetries: this.config.telemetries,
            allowCookies: this.config.allowCookies,
            enableXRay: this.config.enableXRay
        });
    }

    /**
     * Initialize AWS RUM
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        if (this.isInitialized) {
            console.log('[AWSRumService] Already initialized');
            return true;
        }

        // Check if required configuration is present
        if (!this.config.applicationId) {
            console.warn('[AWSRumService] AWS RUM Application ID not configured - RUM disabled');
            return false;
        }

        if (!this.config.identityPoolId) {
            console.warn('[AWSRumService] AWS RUM Identity Pool ID not configured - RUM disabled');
            return false;
        }

        try {
            console.log('[AWSRumService] Initializing AWS RUM...');

            // Create RUM configuration
            const rumConfig = {
                sessionSampleRate: this.config.sessionSampleRate,
                identityPoolId: this.config.identityPoolId,
                endpoint: this.config.endpoint,
                telemetries: this.config.telemetries,
                allowCookies: this.config.allowCookies,
                enableXRay: this.config.enableXRay
            };

            // Initialize AWS RUM
            this.awsRum = new AwsRum(
                this.config.applicationId,
                this.config.applicationVersion,
                this.config.applicationRegion,
                rumConfig
            );

            this.isInitialized = true;

            console.log('[AWSRumService] AWS RUM initialized successfully');

            // Record initialization event
            this.recordEvent('rum_initialized', {
                applicationVersion: this.config.applicationVersion,
                sessionSampleRate: this.config.sessionSampleRate,
                telemetries: this.config.telemetries.join(','),
                timestamp: new Date().toISOString()
            });

            return true;
        } catch (error) {
            console.error('[AWSRumService] Failed to initialize AWS RUM:', error);
            this.isInitialized = false;
            return false;
        }
    }

    /**
     * Check if RUM is available and initialized
     * @returns {boolean} Availability status
     */
    isAvailable() {
        return this.isInitialized && this.awsRum !== null;
    }

    // =============================================================================
    // CUSTOM EVENTS AND METRICS
    // =============================================================================

    /**
     * Record a custom event
     * @param {string} name - Event name
     * @param {Object} details - Event details
     */
    recordEvent(name, details = {}) {
        if (!this.isAvailable()) return;

        try {
            this.awsRum.recordEvent(name, details);
            console.log(`[AWSRumService] Recorded event: ${name}`, details);
        } catch (error) {
            console.error(`[AWSRumService] Failed to record event ${name}:`, error);
        }
    }

    /**
     * Add user attributes to the session
     * @param {Object} attributes - User attributes
     */
    addUserAttributes(attributes) {
        if (!this.isAvailable()) return;

        try {
            this.awsRum.addUserAttributes(attributes);
            console.log('[AWSRumService] Added user attributes:', attributes);
        } catch (error) {
            console.error('[AWSRumService] Failed to add user attributes:', error);
        }
    }

    /**
     * Add session attributes
     * @param {Object} attributes - Session attributes
     */
    addSessionAttributes(attributes) {
        if (!this.isAvailable()) return;

        try {
            this.awsRum.addSessionAttributes(attributes);
            console.log('[AWSRumService] Added session attributes:', attributes);
        } catch (error) {
            console.error('[AWSRumService] Failed to add session attributes:', error);
        }
    }

    // =============================================================================
    // PAYMENT FLOW TRACKING
    // =============================================================================

    /**
     * Track payment flow start
     * @param {Object} paymentData - Payment context data
     */
    trackPaymentStart(paymentData = {}) {
        this.recordEvent('payment_flow_start', {
            ...paymentData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            userAgent: navigator.userAgent
        });
    }

    /**
     * Track payment validation
     * @param {Object} validationData - Validation context
     */
    trackPaymentValidation(validationData = {}) {
        this.recordEvent('payment_validation', {
            ...validationData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track payment success
     * @param {Object} successData - Success context
     */
    trackPaymentSuccess(successData = {}) {
        this.recordEvent('payment_success', {
            ...successData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track payment error
     * @param {Object} errorData - Error context
     */
    trackPaymentError(errorData = {}) {
        this.recordEvent('payment_error', {
            ...errorData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            userAgent: navigator.userAgent
        });
    }

    /**
     * Track Square SDK events
     * @param {string} eventType - Square event type
     * @param {Object} eventData - Event data
     */
    trackSquareEvent(eventType, eventData = {}) {
        this.recordEvent(`square_${eventType}`, {
            ...eventData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    // =============================================================================
    // REGISTRATION FLOW TRACKING
    // =============================================================================

    /**
     * Track registration step completion
     * @param {string} step - Registration step name
     * @param {Object} stepData - Step context data
     */
    trackRegistrationStep(step, stepData = {}) {
        this.recordEvent('registration_step', {
            step: step,
            ...stepData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track form validation errors
     * @param {string} formName - Form name
     * @param {Array} errors - Validation errors
     */
    trackFormValidationErrors(formName, errors = []) {
        this.recordEvent('form_validation_error', {
            formName: formName,
            errors: errors,
            errorCount: errors.length,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track API call performance
     * @param {string} endpoint - API endpoint
     * @param {string} method - HTTP method
     * @param {number} duration - Request duration in ms
     * @param {number} status - HTTP status code
     * @param {boolean} success - Success status
     */
    trackApiCall(endpoint, method, duration, status, success) {
        this.recordEvent('api_call', {
            endpoint: endpoint,
            method: method,
            duration: duration,
            status: status,
            success: success,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    // =============================================================================
    // USER EXPERIENCE TRACKING
    // =============================================================================

    /**
     * Track user interaction
     * @param {string} action - User action
     * @param {Object} context - Interaction context
     */
    trackUserInteraction(action, context = {}) {
        this.recordEvent('user_interaction', {
            action: action,
            ...context,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track page navigation
     * @param {string} from - Previous page
     * @param {string} to - Current page
     * @param {Object} context - Navigation context
     */
    trackPageNavigation(from, to, context = {}) {
        this.recordEvent('page_navigation', {
            from: from,
            to: to,
            ...context,
            timestamp: new Date().toISOString()
        });
    }

    /**
     * Track performance metrics
     * @param {string} metricName - Metric name
     * @param {number} value - Metric value
     * @param {string} unit - Metric unit
     * @param {Object} context - Additional context
     */
    trackPerformanceMetric(metricName, value, unit = 'ms', context = {}) {
        this.recordEvent('performance_metric', {
            metricName: metricName,
            value: value,
            unit: unit,
            ...context,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    // =============================================================================
    // ERROR TRACKING
    // =============================================================================

    /**
     * Track JavaScript errors
     * @param {Error} error - Error object
     * @param {Object} context - Error context
     */
    trackError(error, context = {}) {
        this.recordEvent('javascript_error', {
            message: error.message,
            name: error.name,
            stack: error.stack,
            ...context,
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            userAgent: navigator.userAgent
        });
    }

    /**
     * Track network errors
     * @param {string} url - Request URL
     * @param {string} method - HTTP method
     * @param {number} status - HTTP status
     * @param {string} error - Error message
     * @param {Object} context - Additional context
     */
    trackNetworkError(url, method, status, error, context = {}) {
        this.recordEvent('network_error', {
            url: url,
            method: method,
            status: status,
            error: error,
            ...context,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    // =============================================================================
    // BUSINESS METRICS
    // =============================================================================

    /**
     * Track conversion funnel steps
     * @param {string} funnelStep - Funnel step name
     * @param {Object} stepData - Step data
     */
    trackFunnelStep(funnelStep, stepData = {}) {
        this.recordEvent('funnel_step', {
            funnelStep: funnelStep,
            ...stepData,
            timestamp: new Date().toISOString(),
            page: window.location.pathname
        });
    }

    /**
     * Track user drop-off points
     * @param {string} dropOffPoint - Where user dropped off
     * @param {Object} context - Drop-off context
     */
    trackDropOff(dropOffPoint, context = {}) {
        this.recordEvent('user_drop_off', {
            dropOffPoint: dropOffPoint,
            ...context,
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            sessionDuration: Date.now() - (window.sessionStartTime || Date.now())
        });
    }

    // =============================================================================
    // UTILITY METHODS
    // =============================================================================

    /**
     * Get current session ID
     * @returns {string|null} Session ID
     */
    getSessionId() {
        if (!this.isAvailable()) return null;
        
        try {
            return this.awsRum.getSessionId();
        } catch (error) {
            console.error('[AWSRumService] Failed to get session ID:', error);
            return null;
        }
    }

    /**
     * Enable or disable RUM
     * @param {boolean} enable - Enable status
     */
    setEnabled(enable) {
        if (!this.isAvailable()) return;

        try {
            if (enable) {
                this.awsRum.enable();
            } else {
                this.awsRum.disable();
            }
            console.log(`[AWSRumService] RUM ${enable ? 'enabled' : 'disabled'}`);
        } catch (error) {
            console.error(`[AWSRumService] Failed to ${enable ? 'enable' : 'disable'} RUM:`, error);
        }
    }

    /**
     * Flush pending events
     */
    flush() {
        if (!this.isAvailable()) return;

        try {
            this.awsRum.recordEvent('session_end', {
                timestamp: new Date().toISOString(),
                page: window.location.pathname
            });
        } catch (error) {
            console.error('[AWSRumService] Failed to flush events:', error);
        }
    }
}

// Create and export singleton instance
const awsRumService = new AWSRumService();
export default awsRumService;